import styled from "@emotion/styled";
import AutocompletePrimitive from "../../../primitives/Autocomplete";

// Shared accent for the listbox scrollbar thumb and the active option
// highlight so they stay visually in sync (native-select-like).
const DROPDOWN_ACCENT = "#7A7A7A";

/**
 * Styled Autocomplete used by ERPSBaseAutoComplete.
 * Uses the same CSS as ERPSBaseTextField so the field renders identically
 * (shape, height, font, hover/focus), while exposing the selected value as
 * real, selectable/copyable input text.
 */
const Autocomplete = styled(AutocompletePrimitive)((props) => ({
  borderWidth: "1px  ",
  borderRadius: "60px ",
  height: "35px ",
  marginTop: !props?.hasMargin ? "2rem " : undefined ,
  textTransform: "none ",
  fontSize: "14px ",
  fontFamily: "Roboto Regular",

  "&:MuiOutlinedInput-root:hover": {
    borderColor: "#2A2C2E !important",
  },
  "&:MuiInputBase-root-MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
    { borderColor: "#2A2C2E !important" },

  // MUI renders the label as real text in two places: the floating
  // .MuiInputLabel-root and the notch <legend> (which sits AFTER the input).
  // A full-page "select all + copy" would therefore emit the label after the
  // value ("value label") or duplicate it. Exclude both from the selection so
  // the only copyable label comes from VisuallyHiddenCopyText, which is placed
  // before the input to produce "label value".
  "& .MuiInputLabel-root": { userSelect: "none" },
  "& .MuiOutlinedInput-notchedOutline legend": { userSelect: "none" },
}));

/**
 * Portaled dropdown props for Autocomplete.
 * Kept here (not inline in the component) so list styling stays with styles.
 * Applied via componentsProps so MUI `sx` specificity matches the previous look.
 */
const autocompleteDropdownComponentsProps = {
  // Native <select> popups grow to the longest option; MUI defaults to
  // the input width. Size to content, but never narrower than the field.
  popper: {
    style: { width: "fit-content" },
    placement: "bottom-start",
    modifiers: [
      {
        name: "minWidthToReference",
        enabled: true,
        phase: "beforeWrite",
        requires: ["computeStyles"],
        fn: ({ state }) => {
          state.styles.popper.minWidth = `${state.rects.reference.width}px`;
        },
      },
    ],
  },
  paper: {
    sx: {
      // Tight, native-select-like list (no gaps between values)
      "& .MuiAutocomplete-listbox": {
        paddingTop: 0,
        paddingBottom: 0,
        "&::-webkit-scrollbar": { width: "8px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: DROPDOWN_ACCENT,
          borderRadius: "4px",
        },
        scrollbarColor: `${DROPDOWN_ACCENT} transparent`,
      },
      // Nest under listbox so these beat MUI's option-level selected/hover
      // rules (same accent for selected and keyboard/hover focus).
      "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
        fontSize: "14px",
        fontFamily: "Roboto Regular",
        color: "rgba(0, 0, 0, 0.87)",
        // Fixed row height so the leading blank ("" label) option is
        // still visible/clickable, like the native select.
        minHeight: "24px",
        padding: "2px 8px",
        whiteSpace: "nowrap",
        "&[aria-selected='true']": {
          backgroundColor: DROPDOWN_ACCENT,
          color: "#ffffff",
        },
        "&.Mui-focused, &.Mui-focusVisible": {
          backgroundColor: DROPDOWN_ACCENT,
          color: "#ffffff",
        },
        "&[aria-selected='true'].Mui-focused, &[aria-selected='true'].Mui-focusVisible":
          {
            backgroundColor: DROPDOWN_ACCENT,
            color: "#ffffff",
          },
        "&:hover": {
          backgroundColor: DROPDOWN_ACCENT,
          color: "#ffffff",
        },
        "&[aria-selected='true']:hover": {
          backgroundColor: DROPDOWN_ACCENT,
          color: "#ffffff",
        },
      },
      // When focus/hover moves to another option, clear the selected fill
      // so only one row stays highlighted (native <select>-like).
      "& .MuiAutocomplete-listbox:has(.MuiAutocomplete-option.Mui-focused:not([aria-selected='true'])) .MuiAutocomplete-option[aria-selected='true']":
        {
          backgroundColor: "transparent",
          color: "rgba(0, 0, 0, 0.87)",
        },
      "& .MuiAutocomplete-listbox:has(.MuiAutocomplete-option.Mui-focused:not([aria-selected='true'])) .MuiAutocomplete-option[aria-selected='true']:hover":
        {
          backgroundColor: "transparent",
          color: "rgba(0, 0, 0, 0.87)",
        },
    },
  },
};

/**
 * Accessibility-hidden, but selectable/copyable, text node.
 *
 * Visually clipped (not display:none / visibility:hidden, which would be
 * excluded from copy) so a full-page "select all + copy" emits it. Rendered
 * before the input to force the copied order "label value", since the floating
 * MUI label is not reliably part of the document selection.
 */
const VisuallyHiddenCopyText = styled("span")({
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
});

export default Autocomplete;
export { VisuallyHiddenCopyText, autocompleteDropdownComponentsProps };
